import { defineStore } from "pinia"
import { SafeArea } from "capacitor-plugin-safe-area"
import { Device } from "@capacitor/device"
import { Capacitor } from "@capacitor/core"
import { reactive, ref } from "vue"
import { isNativeDevice, url } from "../helper"
import api from "../composables/useApi"
import { isLoggedIn } from "../composables/useAuth"

interface DeviceInfo {
	model?: string
	isVirtual?: boolean
	manufacturer?: string
	osVersion?: string
	operatingSystem?: string
	platform?: string
	webViewVersion?: string
}

interface RegisteredDevice {
	device_id: string
	device_name: string
	last_active: number
	created: number
}

interface DeviceListResponse {
	devices: RegisteredDevice[]
	max_devices: number
}

interface RegisterDeviceResponse {
	success: boolean
	device_id: string
	message: string
}

export const useDeviceManager = defineStore("useDeviceManager", () => {
	const operatingSystem = ref<string | undefined>(undefined)
	const deviceInfo = reactive<DeviceInfo>({})
	const registeredDevices = ref<RegisteredDevice[]>([])
	const maxDevices = ref(3)
	const loading = ref(false)
	const error = ref<string | null>(null)

	const initializeDevice = async () => {
		// Only get device info on native platforms
		operatingSystem.value = (await Device.getInfo()).operatingSystem
		Object.assign(deviceInfo, await Device.getInfo())
		if (Capacitor.isNativePlatform()) {
			operatingSystem.value = deviceInfo.operatingSystem
		} else {
			// For web, use browser detection
			operatingSystem.value = "web"
		}

		// Add OS class to body
		if (operatingSystem.value) {
			if (!isNativeDevice) {
				document.body.classList.add(`is-${operatingSystem.value}`)
			} else {
				document.body.classList.add(`is-web`)
			}
		}

		// console.log("test OS", operatingSystem.value)

		// Load iOS-specific styles
		if (operatingSystem.value === "ios") {
			document.body.classList.add("is-ios")
			await import("../scss/ios.scss")
		} else if (operatingSystem.value === "android") {
			document.body.classList.add("is-android")
			await import("../scss/android.scss")
		}
	}

	const initializeSafeArea = async () => {
		// Only get safe area on native platforms
		if (!Capacitor.isNativePlatform()) {
			return
		}

		// Get initial safe area insets
		SafeArea.getSafeAreaInsets().then(({ insets }) => {
			// console.log(insets)
			for (const [key, value] of Object.entries(insets)) {
				document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${value}px`)
			}
		})

		// Get status bar height
		// SafeArea.getStatusBarHeight().then(({ statusBarHeight }) => {
		// 	console.log(statusBarHeight, "statusbarHeight")
		// })

		// Remove existing listeners
		await SafeArea.removeAllListeners()

		// Listen for safe area changes
		await SafeArea.addListener("safeAreaChanged", (data) => {
			const { insets } = data
			for (const [key, value] of Object.entries(insets)) {
				document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${value}px`)
			}
		})
	}

	/**
	 * Register a new device
	 */
	const registerDevice = async (deviceId: string, deviceName: string): Promise<boolean> => {
		if (!isLoggedIn()) {
			error.value = "User not logged in"
			return false
		}

		loading.value = true
		error.value = null

		try {
			const response = await api.post<RegisterDeviceResponse>(`${url}/api/device/register`, {
				device_id: deviceId,
				device_name: deviceName,
			})

			if (response.data.success) {
				// Refresh device list after registration
				await listDevices()
				return true
			} else {
				error.value = "Failed to register device"
				return false
			}
		} catch (err: any) {
			error.value = err.response?.data?.error || "Failed to register device"
			console.error("Device registration error:", err)
			return false
		} finally {
			loading.value = false
		}
	}

	/**
	 * List all user devices
	 */
	const listDevices = async (): Promise<RegisteredDevice[]> => {
		if (!isLoggedIn()) {
			error.value = "User not logged in"
			return []
		}

		loading.value = true
		error.value = null

		try {
			const response = await api.get<DeviceListResponse>(`${url}/api/device/list`)

			registeredDevices.value = response.data.devices
			maxDevices.value = response.data.max_devices
			return response.data.devices
		} catch (err: any) {
			error.value = err.response?.data?.error || "Failed to fetch devices"
			console.error("Device list error:", err)
			return []
		} finally {
			loading.value = false
		}
	}

	/**
	 * Deactivate a device
	 */
	const deactivateDevice = async (deviceId: string): Promise<boolean> => {
		if (!isLoggedIn()) {
			error.value = "User not logged in"
			return false
		}

		loading.value = true
		error.value = null

		try {
			await api.post(`${url}/api/device/deactivate`, {
				device_id: deviceId,
			})

			// Refresh device list after deactivation
			await listDevices()
			return true
		} catch (err: any) {
			error.value = err.response?.data?.error || "Failed to deactivate device"
			console.error("Device deactivation error:", err)
			return false
		} finally {
			loading.value = false
		}
	}

	/**
	 * Generate a unique device ID for the current device
	 * First tries to use Capacitor Device.getId() for native platforms,
	 * then falls back to localStorage or random generation
	 */
	const generateDeviceId = async (): Promise<string> => {
		// Check if device ID already exists in localStorage
		let deviceId = localStorage.getItem("device_id")

		if (!deviceId) {
			// Try to get device ID from Capacitor (works on native platforms)
			try {
				const deviceInfo = await Device.getId()
				if (deviceInfo.identifier) {
					deviceId = deviceInfo.identifier
					localStorage.setItem("device_id", deviceId)
					return deviceId
				}
			} catch (error) {
				console.log("Capacitor Device.getId() not available, using fallback")
			}

			// Fallback: Generate new device ID using timestamp + random string
			deviceId = `device-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
			localStorage.setItem("device_id", deviceId)
		}

		return deviceId
	}

	/**
	 * Get the current device ID (async version)
	 * Returns cached ID from localStorage or generates a new one
	 */
	const getCurrentDeviceId = async (): Promise<string> => {
		const cachedId = localStorage.getItem("device_id")
		if (cachedId) {
			return cachedId
		}
		return await generateDeviceId()
	}

	/**
	 * Get a friendly device name based on platform
	 */
	const getDeviceName = (): string => {
		const userAgent = navigator.userAgent.toLowerCase()

		if (userAgent.includes("android")) {
			return "Android Device"
		} else if (userAgent.includes("iphone") || userAgent.includes("ipad")) {
			return "iOS Device"
		} else if (userAgent.includes("mac")) {
			return "Mac Computer"
		} else if (userAgent.includes("windows")) {
			return "Windows Computer"
		} else if (userAgent.includes("linux")) {
			return "Linux Computer"
		} else {
			return "Web Browser"
		}
	}

	/**
	 * Check if the current device is registered
	 * Returns true if the current device ID is in the list of registered devices
	 */
	const isCurrentDeviceRegistered = async (): Promise<boolean> => {
		const currentDeviceId = await getCurrentDeviceId()
		const isRegistered =
			registeredDevices.value.some((device) => device.device_id === currentDeviceId) &&
			registeredDevices.value.length <= maxDevices.value
		if (registeredDevices.value.length < maxDevices.value) {
			// try registering if not at max devices
			try {
				await registerDevice(currentDeviceId, getDeviceName())
				return true
			} catch (error) {
				console.error("Error registering device:", error)
				return false
			}
		}
		return isRegistered
	}

	return {
		operatingSystem,
		deviceInfo,
		registeredDevices,
		maxDevices,
		loading,
		error,
		initializeDevice,
		initializeSafeArea,
		registerDevice,
		listDevices,
		deactivateDevice,
		generateDeviceId,
		getCurrentDeviceId,
		getDeviceName,
		isCurrentDeviceRegistered,
	}
})
